import React from 'react';
import { Dropdown, Grid, Input, Segment } from 'semantic-ui-react';
import styled from 'styled-components';

import { FieldLabel } from '../../../styles';
import Button from '../../../components/Button';
import { maskCpfOrCnpj } from '../../../services/masks';

import { FiltersState } from '../types';
import EstateUnitFilter from '../../../components/EstateUnitFilter';

type Props = {
  isEstateUser: boolean;
  estateOptions: Array<{ key: string; text: string; value: string }>;
  filters: FiltersState;
  setFilters: React.Dispatch<React.SetStateAction<FiltersState>>;
  statusOptions: Array<{ key: string; value: string; text: string }>;
  onSearch: () => void;
  onClear: () => void;
  onExport: () => void;
  exportLoading: boolean;
};

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 991px) {
    justify-content: flex-start;
  }

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const searchTypeOptions = [
  { key: 'payer', value: 'payer', text: 'Nome' },
  { key: 'document', value: 'document', text: 'CPF' },
  { key: 'email', value: 'email', text: 'E-mail' }
];

const FiltersBar: React.FC<Props> = ({
  isEstateUser,
  estateOptions,
  filters,
  setFilters,
  statusOptions,
  onSearch,
  onClear,
  onExport,
  exportLoading
}) => {
  return (
    <Segment>
      <Grid stackable>
        <Grid.Row>
          {!isEstateUser && (
            <Grid.Column computer={4} tablet={8} mobile={16}>
              <FieldLabel>Imobiliária</FieldLabel>
              <div style={{ width: '100%' }}>
                <Dropdown
                  style={{ width: '100%' }}
                  value={filters.estateId || ''}
                  placeholder="Selecionar..."
                  options={estateOptions}
                  search
                  selection
                  clearable
                  onChange={(_, { value }) =>
                    setFilters(prev => ({
                      ...prev,
                      estateId: (value as string) || '',
                      estateUnitId: ''
                    }))
                  }
                />
              </div>
            </Grid.Column>
          )}

          <Grid.Column computer={4} tablet={8} mobile={16}>
            <FieldLabel>Pesquisar por</FieldLabel>
            <div style={{ width: '100%' }}>
              <Dropdown
                style={{ width: '100%' }}
                value={filters.type}
                placeholder="Selecionar..."
                options={searchTypeOptions}
                search
                selection
                onChange={(_, { value }) =>
                  setFilters(prev => ({
                    ...prev,
                    type: value as FiltersState['type'],
                    argument: ''
                  }))
                }
              />
            </div>
          </Grid.Column>

          <Grid.Column computer={isEstateUser ? 12 : 8} tablet={16} mobile={16}>
            <FieldLabel>Termo de pesquisa</FieldLabel>
            <Input
              fluid
              value={filters.argument}
              onChange={(_, { value }) => {
                if (filters.type === 'document') {
                  setFilters(prev => ({ ...prev, argument: maskCpfOrCnpj(value) }));
                  return;
                }
                setFilters(prev => ({ ...prev, argument: value }));
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  onSearch();
                }
              }}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <EstateUnitFilter
            columnWidth={4}
            estateId={filters.estateId}
            value={filters.estateUnitId}
            onChange={value =>
              setFilters(prev => ({ ...prev, estateUnitId: value }))
            }
          />

          <Grid.Column computer={4} tablet={8} mobile={16}>
            <FieldLabel>Status</FieldLabel>
            <div style={{ width: '100%' }}>
              <Dropdown
                style={{ width: '100%' }}
                value={filters.status || ''}
                placeholder="Selecionar..."
                options={statusOptions}
                search
                selection
                clearable
                onChange={(_, { value }) =>
                  setFilters(prev => ({ ...prev, status: (value as string) || '' }))
                }
              />
            </div>
          </Grid.Column>

          <Grid.Column computer={4} tablet={8} mobile={16}>
            <FieldLabel>Data Inicial</FieldLabel>
            <Input
              fluid
              type="date"
              value={filters.startDate}
              onChange={(_, { value }) => setFilters(prev => ({ ...prev, startDate: value }))}
            />
          </Grid.Column>

          <Grid.Column computer={4} tablet={8} mobile={16}>
            <FieldLabel>Data Final</FieldLabel>
            <Input
              fluid
              type="date"
              value={filters.endDate}
              onChange={(_, { value }) => setFilters(prev => ({ ...prev, endDate: value }))}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column computer={16} tablet={16} mobile={16}>
            <ActionsWrapper>
              <Button
                light
                type="button"
                text="Exportar relatório"
                onClick={onExport}
                loading={exportLoading}
                disabled={exportLoading}
              />
              <Button type="submit" text="Pesquisar" onClick={() => onSearch()} />
              <Button light type="button" text="Limpar" onClick={onClear} />
            </ActionsWrapper>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
};

export default FiltersBar;
